import { isArray } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { stringFormat, trim } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { ChangeEvent, TextareaHTMLAttributes, useContext, useRef } from 'react';
import { useEffectCustom } from '../../../hooks';
import { FormContext } from '../../context';
import { omit } from '../../helps/utils';
import './style.less';
type TextareaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'defaultValue' | 'onChange'
> & {
  onChange?: (value: string, event: ChangeEvent) => void;
  showCount?: boolean;
};

type TextareaPropsInner = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  value: string;
  onChange: (value: string) => void;
  name: string;
  format?: number[] | ((data?: string) => string);
};

/**
 * textarea组件的 value、defaultValue 属性，不提供给Form外部使用
 * 如果需要设置value和defaultValue属性，请使用form方法设置
 * @param props
 * @returns
 */
export const Textarea = (props: TextareaProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const newProps = props as unknown as TextareaPropsInner;
  const { form } = useContext(FormContext);
  const fieldValue = form.getFieldValue(newProps.name);
  const cpLockRef = useRef(false);

  const onInputChange = hooks.useCallbackRef((event) => {
    if (!cpLockRef.current) {
      let value = event.target['value'] as string;
      if (isArray(newProps.format) && newProps.format.length > 0) {
        value = trim(value);
      }
      newProps.onChange(value);
    }
  });

  useEffectCustom(() => {
    if (inputRef.current) {
      const value = String(fieldValue || '');
      if (typeof newProps.format === 'function') {
        inputRef.current.value = newProps.format(value);
      } else if (isArray(newProps.format) && newProps.format.length > 0) {
        inputRef.current.value = stringFormat(value, newProps.format).join(' ');
      } else {
        inputRef.current.value = value;
      }
    }
  }, [fieldValue]);

  const compositionstartHandle = hooks.useCallbackRef(() => {
    cpLockRef.current = true;
  });
  const compositionendHandle = hooks.useCallbackRef((event) => {
    cpLockRef.current = false;
    onInputChange(event as ChangeEvent);
  });

  useEffectCustom(() => {
    if (inputRef.current) {
      inputRef.current.addEventListener('compositionstart', compositionstartHandle);
      inputRef.current.addEventListener('compositionend', compositionendHandle);
    }
  }, []);

  const convertProps = omit(newProps, ['value', 'suffix', 'showCount', 'format']);
  const showCount = props.showCount && props.maxLength && props.maxLength > 0;

  return (
    <div className="v-textarea-wrapper">
      <textarea
        {...convertProps}
        className={classNames('v-textarea', newProps.className)}
        onChange={onInputChange}
        ref={inputRef}
      />
      {showCount ? (
        <div className="v-textarea-min-max">
          {newProps.value?.length || 0}/{props.maxLength}
        </div>
      ) : null}
    </div>
  );
};

Textarea.domTypeName = 'Textarea';
